import React, { useState } from 'react';
import {
  IndianRupee, Gauge, Droplets,
  FileText, FileSpreadsheet, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ─── Hardcoded report data ───────────────────────────────────────── */

const SUMMARY_CARDS = [
  { icon: IndianRupee, bg: 'bg-violet-500', label: 'Monthly Fuel Cost',    value: '₹2,77,720', sub: 'May 2026'    },
  { icon: Gauge,       bg: 'bg-orange-500', label: 'Average Mileage',      value: '7.45 km/L', sub: 'Fleet avg'   },
  { icon: Droplets,    bg: 'bg-blue-500',   label: 'Total Fuel Quantity',  value: '308 L',     sub: 'This month'  },
];

const VEHICLE_REPORT = [
  { vehicle: 'BUS001', driver: 'Rahul Sharma', litres: 118, cost: 106020, mileage: 8.18 },
  { vehicle: 'BUS002', driver: 'Amit Verma',   litres: 87,  cost: 78180,  mileage: 7.14 },
  { vehicle: 'BUS003', driver: 'Rahul Sharma', litres: 55,  cost: 49500,  mileage: 5.92 },
  { vehicle: 'BUS004', driver: 'Suresh Kumar', litres: 48,  cost: 44020,  mileage: 4.82 },
];

const MONTHLY_REPORT = [
  { month: 'Jan 2026', cost: 310000, litres: 340, mileage: 7.9  },
  { month: 'Feb 2026', cost: 295000, litres: 322, mileage: 8.1  },
  { month: 'Mar 2026', cost: 317000, litres: 348, mileage: 7.6  },
  { month: 'Apr 2026', cost: 316800, litres: 352, mileage: 7.8  },
  { month: 'May 2026', cost: 277720, litres: 308, mileage: 7.45 },
];

const MILEAGE_REPORT = [
  { vehicle: 'BUS001', mileage: 8.18, status: 'Efficient',     service: '15 Apr 2026' },
  { vehicle: 'BUS002', mileage: 7.14, status: 'Normal',        service: '02 Mar 2026' },
  { vehicle: 'BUS003', mileage: 5.92, status: 'Low Efficiency', service: '10 Jan 2026' },
  { vehicle: 'BUS004', mileage: 4.82, status: 'Critical',      service: '05 Nov 2025' },
];

const STATUS_STYLE = {
  Efficient:       'bg-green-100 text-green-700',
  Normal:          'bg-blue-100 text-blue-700',
  'Low Efficiency': 'bg-amber-100 text-amber-700',
  Critical:        'bg-red-100 text-red-700',
};

const MILEAGE_COLOR = (v) =>
  v >= 8 ? 'text-green-600' :
  v >= 7 ? 'text-gray-700'  :
  v >= 6 ? 'text-amber-600' :
  'text-red-600';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(n);

/* ─── Small components ────────────────────────────────────────────── */

const SummaryCard = ({ icon: Icon, bg, label, value, sub }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
    <div className={`${bg} p-3 rounded-xl shrink-0`}>
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium mb-0.5">{label}</p>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
    </div>
  </div>
);

const ExportBar = ({ onPdf, onExcel, pdfLoading }) => (
  <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl">
    <button
      onClick={onPdf}
      disabled={pdfLoading}
      className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-red-50 text-red-600 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {pdfLoading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <FileText className="h-3.5 w-3.5" />}
      {pdfLoading ? 'Generating…' : 'Export PDF'}
    </button>
    <button
      onClick={onExcel}
      className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-green-50 text-green-600 border border-green-200 rounded-xl hover:bg-green-100 transition-colors"
    >
      <FileSpreadsheet className="h-3.5 w-3.5" />
      Export Excel
    </button>
  </div>
);

const ReportSection = ({ title, description, defaultOpen = false, children, onPdf, onExcel, pdfLoading }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen((v) => !v)}
      >
        <div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <p className="text-xs text-gray-400 mt-0.5">{description}</p>
        </div>
        {open
          ? <ChevronUp   className="h-4 w-4 text-gray-400 shrink-0" />
          : <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-gray-100">
          {children}
          <ExportBar onPdf={onPdf} onExcel={onExcel} pdfLoading={pdfLoading} />
        </div>
      )}
    </div>
  );
};

/* ─── Excel generators per report type ───────────────────────────── */

const downloadExcel = (sheetName, columns, rows, fileName) => {
  const header = columns.map((c) => c.label);
  const data   = rows.map((r) => columns.map((c) => c.value(r)));
  const ws     = XLSX.utils.aoa_to_sheet([header, ...data]);

  // Column widths
  ws['!cols'] = columns.map((c) => ({ wch: c.width || 18 }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, fileName);
};

const exportVehicleExcel = () => {
  downloadExcel(
    'Vehicle Report',
    [
      { label: 'Vehicle',            value: (r) => r.vehicle, width: 12 },
      { label: 'Driver',             value: (r) => r.driver,  width: 20 },
      { label: 'Total Litres (L)',   value: (r) => r.litres,  width: 18 },
      { label: 'Total Cost (₹)',     value: (r) => r.cost,    width: 18 },
      { label: 'Avg Mileage (km/L)', value: (r) => r.mileage, width: 22 },
    ],
    VEHICLE_REPORT,
    `vehicle-fuel-report-${Date.now()}.xlsx`,
  );
};

const exportMonthlyExcel = () => {
  const rows = MONTHLY_REPORT.map((r, i) => {
    const prev  = MONTHLY_REPORT[i - 1];
    const delta = prev ? (((r.cost - prev.cost) / prev.cost) * 100).toFixed(1) + '%' : '—';
    return { ...r, delta };
  });
  downloadExcel(
    'Monthly Expenses',
    [
      { label: 'Month',           value: (r) => r.month,   width: 14 },
      { label: 'Total Cost (₹)',  value: (r) => r.cost,    width: 18 },
      { label: 'MoM Change',      value: (r) => r.delta,   width: 14 },
      { label: 'Total Litres (L)',value: (r) => r.litres,  width: 18 },
      { label: 'Avg Mileage',     value: (r) => r.mileage, width: 14 },
    ],
    rows,
    `monthly-expense-report-${Date.now()}.xlsx`,
  );
};

const exportMileageExcel = () => {
  downloadExcel(
    'Mileage Report',
    [
      { label: 'Vehicle',             value: (r) => r.vehicle, width: 12 },
      { label: 'Avg Mileage (km/L)', value: (r) => r.mileage, width: 22 },
      { label: 'Efficiency Status',   value: (r) => r.status,  width: 20 },
      { label: 'Last Service',        value: (r) => r.service, width: 16 },
    ],
    MILEAGE_REPORT,
    `mileage-report-${Date.now()}.xlsx`,
  );
};

/* ─── Main ────────────────────────────────────────────────────────── */

const FuelReports = () => {
  const [pdfLoading, setPdfLoading] = useState(false);

  const handlePdfExport = () => {
    setPdfLoading(true);
    try {
      const doc = new jsPDF();

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Fuel Management Report', 105, 18, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`, 105, 26, { align: 'center' });

      /* ── Summary ── */
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Summary  (May 2026)', 14, 38);
      autoTable(doc, {
        startY: 43,
        head: [['Metric', 'Value']],
        body: [
          ['Monthly Fuel Cost',   '₹2,77,720'],
          ['Total Fuel Quantity', '308 L'],
          ['Average Mileage',    '7.45 km/L'],
        ],
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229] },
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } },
      });

      /* ── Vehicle Report ── */
      const y1 = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Vehicle-wise Fuel Report', 14, y1);
      autoTable(doc, {
        startY: y1 + 5,
        head: [['Vehicle', 'Driver', 'Litres (L)', 'Cost (₹)', 'Mileage (km/L)']],
        body: VEHICLE_REPORT.map((r) => [r.vehicle, r.driver, r.litres, `₹${fmt(r.cost)}`, r.mileage]),
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246] },
        styles: { fontSize: 9 },
      });

      /* ── Monthly Expense ── */
      const y2 = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Monthly Expense Report', 14, y2);
      autoTable(doc, {
        startY: y2 + 5,
        head: [['Month', 'Cost (₹)', 'MoM Change', 'Litres (L)', 'Mileage']],
        body: MONTHLY_REPORT.map((r, i) => {
          const prev  = MONTHLY_REPORT[i - 1];
          const delta = prev ? ((r.cost - prev.cost) / prev.cost * 100).toFixed(1) + '%' : '—';
          return [r.month, `₹${fmt(r.cost)}`, delta, `${r.litres} L`, `${r.mileage} km/L`];
        }),
        theme: 'grid',
        headStyles: { fillColor: [16, 185, 129] },
        styles: { fontSize: 9 },
      });

      /* ── Mileage Report ── */
      const y3 = doc.lastAutoTable.finalY + 12;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Mileage Report', 14, y3);
      autoTable(doc, {
        startY: y3 + 5,
        head: [['Vehicle', 'Mileage (km/L)', 'Efficiency Status', 'Last Service']],
        body: MILEAGE_REPORT.map((r) => [r.vehicle, r.mileage, r.status, r.service]),
        theme: 'grid',
        headStyles: { fillColor: [245, 158, 11] },
        styles: { fontSize: 9 },
      });

      doc.save(`fuel-report-${Date.now()}.pdf`);
    } catch {
      alert('PDF export failed. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="space-y-6">

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Reports</h2>
        <p className="text-sm text-gray-500 mt-0.5">Export and audit transport fuel records.</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SUMMARY_CARDS.map((c) => <SummaryCard key={c.label} {...c} />)}
      </div>

      {/* Report 1 — Vehicle-wise */}
      <ReportSection
        title="Vehicle-wise Fuel Report"
        description="Per-vehicle usage, mileage and monthly spend."
        defaultOpen
        onPdf={handlePdfExport}
        onExcel={exportVehicleExcel}
        pdfLoading={pdfLoading}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Vehicle','Driver','Total Litres','Total Cost','Avg Mileage (km/L)'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {VEHICLE_REPORT.map((r) => (
                <tr key={r.vehicle} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-gray-900">{r.vehicle}</td>
                  <td className="px-5 py-3.5 text-gray-700">{r.driver}</td>
                  <td className="px-5 py-3.5 text-gray-700">{r.litres} L</td>
                  <td className="px-5 py-3.5 text-gray-700">₹{fmt(r.cost)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`font-semibold ${MILEAGE_COLOR(r.mileage)}`}>{r.mileage}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>

      {/* Report 2 — Monthly Expense */}
      <ReportSection
        title="Monthly Expense Report"
        description="Total expenses, trends and month-over-month comparison."
        onPdf={handlePdfExport}
        onExcel={exportMonthlyExcel}
        pdfLoading={pdfLoading}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Month','Total Cost','MoM Change','Total Litres','Avg Mileage'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MONTHLY_REPORT.map((r, i) => {
                const prev  = MONTHLY_REPORT[i - 1];
                const delta = prev ? ((r.cost - prev.cost) / prev.cost) * 100 : null;
                return (
                  <tr key={r.month} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-gray-800">{r.month}</td>
                    <td className="px-5 py-3.5 text-gray-700">₹{fmt(r.cost)}</td>
                    <td className="px-5 py-3.5">
                      {delta !== null ? (
                        <span className={`text-xs font-semibold ${delta > 0 ? 'text-red-500' : 'text-green-600'}`}>
                          {delta > 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(1)}%
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-gray-700">{r.litres} L</td>
                    <td className="px-5 py-3.5 text-gray-700">{r.mileage} km/L</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ReportSection>

      {/* Report 3 — Mileage */}
      <ReportSection
        title="Mileage Report"
        description="Bus efficiency comparison and low-efficiency vehicle identification."
        onPdf={handlePdfExport}
        onExcel={exportMileageExcel}
        pdfLoading={pdfLoading}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {['Vehicle','Avg Mileage (km/L)','Efficiency Status','Last Service'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MILEAGE_REPORT.map((r) => (
                <tr key={r.vehicle} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-gray-900">{r.vehicle}</td>
                  <td className="px-5 py-3.5">
                    <span className={`font-semibold ${MILEAGE_COLOR(r.mileage)}`}>{r.mileage}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-gray-600">{r.service}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>

    </div>
  );
};

export default FuelReports;
